import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Phone, PhoneForwarded, PhoneOff, Smartphone, Users, Voicemail } from 'lucide-react';
import { apiGet, apiPut } from '../../lib/api.js';

const MODES = [
  { id: 'available', label: 'Available', icon: Phone, hint: 'Ring me on web/mobile' },
  { id: 'forward', label: 'Forward to mobile', icon: PhoneForwarded, hint: 'Send to forwarding number' },
  { id: 'voicemail', label: 'Voicemail only', icon: Voicemail, hint: 'Straight to voicemail box' },
  { id: 'round_robin', label: 'Round-robin team', icon: Users, hint: 'Distribute among agents' },
  { id: 'dnd', label: 'Do not disturb', icon: PhoneOff, hint: 'Decline all inbound' },
];

/**
 * Inbound call routing selector ("Available ▾") shown in the global header so it
 * can be changed from any page.
 * @returns {JSX.Element}
 */
export default function RoutingSelect() {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('available');
  const ref = useRef(null);

  useEffect(() => {
    apiGet('/inbound-routing').then((r) => { if (r?.routing?.mode) setMode(r.routing.mode); }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  async function pick(id) {
    setMode(id);
    setOpen(false);
    try { await apiPut('/inbound-routing', { mode: id }); } catch { /* ignore */ }
  }

  const current = MODES.find((m) => m.id === mode);
  const CurrentIcon = current?.icon || Smartphone;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="routing-select"
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-civix-panelLight border border-civix-border/60 text-xs font-medium hover:border-civix-purple/60 transition"
      >
        <CurrentIcon className="w-3.5 h-3.5 text-civix-cyan" />
        <span className="hidden sm:inline text-white/80">{current?.label}</span>
        <ChevronDown className="w-3.5 h-3.5 text-white/50" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 mt-2 w-72 panel p-1 z-50 shadow-glow-purple">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wide text-white/40">Inbound call routing</div>
          {MODES.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.id}
                type="button"
                role="menuitem"
                onClick={() => pick(m.id)}
                className={`block w-full text-left px-3 py-2 rounded-md text-sm transition ${
                  mode === m.id ? 'bg-civix-purple/20 text-white' : 'hover:bg-civix-panelLight text-white/70'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 text-civix-cyan" />
                  <span className="font-medium">{m.label}</span>
                </div>
                <div className="text-[10px] text-white/40 mt-0.5 ml-5">{m.hint}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
